import { AlertTriangle, ImagePlus, Images, Loader2, Trash2, Upload, X } from 'lucide-react';
import { forwardRef, useId, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { Progress } from '@/shared/components/ui/Progress/Progress';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { cn } from '@/shared/utils/cn/cn.util';
import { formatBytes } from '@/shared/utils/format-bytes/format-bytes.util';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { MediaLibraryDialog } from '../MediaLibraryDialog/MediaLibraryDialog';
import { MediaShell } from '../MediaShell/MediaShell';
import {
  MEDIA_ALPHA_BACKDROP_CLASSES,
  MEDIA_DROPZONE_ACTIVE_CLASSES,
  MEDIA_DROPZONE_CLASSES,
  MEDIA_ERROR_CLASSES,
  MEDIA_FACTS_CLASSES,
  MEDIA_FACT_CLASSES,
  MEDIA_IMAGE_CLASSES,
  MEDIA_INVALID_CLASSES,
  MEDIA_PLACEHOLDER_CLASSES,
  MEDIA_PREVIEW_CLASSES,
  MEDIA_PROGRESS_BAR_CLASSES,
  MEDIA_PROGRESS_CLASSES,
  MEDIA_UNAVAILABLE_CLASSES,
} from '../media.tokens';
import { type MediaHandle, type MediaPickerProps } from '../types/media-field.types';

import { useMediaPickerLogic } from './useMediaPickerLogic';

/**
 * One image, chosen by uploading it or by picking one already uploaded.
 *
 * **Self-naming** (`role="group"` + `aria-label`): it renders several named
 * controls — a preview, an upload, a library button, a remove — so one
 * `<Label htmlFor>` could name only one of them and would leave the rest
 * anonymous. `SELF_NAMING_KINDS` carries the same fact for `dynamic-form`.
 *
 * Every state the domain has is drawn in the **same box**, so the control never
 * changes height as it moves between them — a picker that grows when an upload
 * starts pushes the rest of the form down under the operator's cursor.
 *
 * Presentation only (U5): `useMediaPickerLogic` owns every decision.
 */
export const MediaPicker = forwardRef<MediaHandle, MediaPickerProps>(function MediaPicker(
  {
    id,
    className,
    label,
    kind,
    value,
    onValueChange,
    source,
    maxBytes,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const {
    state,
    asset,
    progress,
    errorKey,
    accept,
    canUpload,
    canBrowse,
    canClear,
    retry,
    upload,
    cancelUpload,
    choose,
    clear,
    announcement,
  } = useMediaPickerLogic({
    value,
    kind,
    source,
    maxBytes,
    isDisabled,
    isReadOnly,
    onValueChange,
  });

  const previewRef = useRef<HTMLDivElement>(null);
  const previewButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * `shouldFocusError` calls `field.ref.focus()`, and the element that should
   * take focus is the control's PRIMARY ACTION — not the wrapper the ref happens
   * to be attached to.
   *
   * The `disabled` check is load-bearing, and this component's own spec is what
   * found it missing: a read-only or disabled picker still RENDERS its upload
   * button, and `HTMLButtonElement.focus()` on a disabled button is a silent
   * no-op — so an invalid read-only field was reported by the form and could
   * never be reached. That is the P10.2d defect class arriving through a state
   * rather than through a missing ref.
   *
   * The fallback is the preview, which is a real `<button>` whenever anything
   * can be uploaded and a plain box otherwise. A read-only picker therefore has
   * nothing focusable at all, which is the honest answer: its error is not
   * actionable, and `element` still gives the form something to scroll to.
   */
  const focusTarget = (): HTMLElement | null => {
    const upload = uploadButtonRef.current;
    if (upload !== null && !upload.disabled) return upload;

    return previewButtonRef.current ?? previewRef.current;
  };

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        focusTarget()?.focus();
      },
      get element() {
        return focusTarget();
      },
    }),
    [],
  );

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const size = asset === null ? null : formatBytes(asset.sizeBytes);

  /**
   * What is drawn inside the preview box, in every state.
   *
   * One box for all of them, so the control does not change height as it moves
   * between states — a picker that grows when an upload starts pushes the rest
   * of the form down under the operator's cursor.
   */
  const previewContent =
    state === 'uploading' ? (
      <span className={MEDIA_PROGRESS_CLASSES}>
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        {/*
          Determinate whenever the transport can count bytes, and honestly
          indeterminate when it cannot — `undefined` keeps `Progress` a pulsing
          sliver rather than collapsing it to a bar at 0%.
        */}
        <Progress
          className={MEDIA_PROGRESS_BAR_CLASSES}
          value={progress ?? undefined}
          aria-label={t('upload.uploading')}
        />
        <span>
          {progress === null
            ? t('upload.uploading')
            : t('media.progress', { percent: formatNumber(progress, locale) })}
        </span>
      </span>
    ) : state === 'resolving' ? (
      <span className={MEDIA_PLACEHOLDER_CLASSES}>
        <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
        <span>{t('media.resolving')}</span>
      </span>
    ) : state === 'unavailable' ? (
      <span className={MEDIA_UNAVAILABLE_CLASSES}>
        <AlertTriangle aria-hidden className="h-5 w-5" />
        <span>{t('media.unavailable')}</span>
        <span>{t('media.unavailableHint')}</span>
      </span>
    ) : state === 'ready' && asset !== null ? (
      // `alt=""` and NOT the label: the group is already named, and a screen
      // reader announcing the name twice — once for the group, once for the
      // image inside it — is noise, not information.
      <img src={asset.url} alt="" className={MEDIA_IMAGE_CLASSES} />
    ) : (
      <span className={MEDIA_PLACEHOLDER_CLASSES}>
        <ImagePlus aria-hidden className="h-6 w-6" />
        <span>{t('media.empty')}</span>
        {canUpload ? <span>{t('media.emptyHint')}</span> : null}
      </span>
    );

  return (
    <>
      <MediaShell
        role="group"
        id={id}
        className={className}
        label={label}
        aria-describedby={describedBy}
        isReadOnly={isReadOnly}
        announcement={announcement}
        actions={
          state === 'uploading' ? (
            <Button type="button" variant="outline" size="sm" onClick={cancelUpload}>
              <X aria-hidden className="h-4 w-4" />
              {t('media.cancel')}
            </Button>
          ) : (
            <>
              <Button
                ref={uploadButtonRef}
                type="button"
                variant="outline"
                size="sm"
                disabled={!canUpload}
                aria-label={t('media.uploadFor', { label })}
                onClick={openFilePicker}
                onBlur={onBlur}
              >
                <Upload aria-hidden className="h-4 w-4" />
                {state === 'ready' ? t('media.replace') : t('media.upload')}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canBrowse}
                aria-label={t('media.browseFor', { label })}
                onClick={() => {
                  setIsLibraryOpen(true);
                }}
              >
                <Images aria-hidden className="h-4 w-4" />
                {t('media.browse')}
              </Button>

              {canClear ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t('media.removeFor', { label })}
                  onClick={clear}
                >
                  <Trash2 aria-hidden className="h-4 w-4" />
                  {t('media.remove')}
                </Button>
              ) : null}

              {/* Here rather than inside the unavailable preview: that box is a
                  `<button>` whenever it can take an upload, and a button inside
                  a button is invalid HTML. */}
              {retry === undefined ? null : (
                <Button type="button" variant="ghost" size="sm" onClick={retry}>
                  {t('media.retry')}
                </Button>
              )}
            </>
          )
        }
        error={
          errorKey === null ? null : (
            <p role="alert" className={MEDIA_ERROR_CLASSES}>
              {t(errorKey)}
            </p>
          )
        }
      >
        {/*
          One hidden input for both the button and the drop zone. Reset on
          `change`, so re-picking the SAME file after a failure fires the event
          again — without it a retry of the identical file is silently ignored.
        */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          disabled={!canUpload}
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => {
            const { files } = event.target;
            if (files !== null) {
              void upload(files);
            }
            event.target.value = '';
          }}
        />

        {/*
          The preview box is a real `<button>` whenever it can accept an upload,
          and a plain box otherwise.

          Not a `div` with `tabIndex` and click handlers: that is a control
          assistive tech cannot see, and `jsx-a11y` rejects it at error level
          here. Making it a button also gives the operator the affordance they
          expect — clicking the picture opens the file chooser — and gives the
          ref handle something focusable to land on.

          It is why `retry` lives in the actions row rather than inside the
          unavailable state: a button nested inside a button is invalid HTML, and
          the browser's own behaviour for it is undefined.
        */}
        {canUpload ? (
          <button
            ref={previewButtonRef}
            type="button"
            // Its OWN name, never the upload button's: two controls sharing one
            // accessible name is two rows a screen reader cannot tell apart, and
            // this component's own spec found them ambiguous immediately.
            aria-label={t('media.dropzoneFor', { label })}
            className={cn(
              MEDIA_PREVIEW_CLASSES,
              MEDIA_DROPZONE_CLASSES,
              state === 'ready' && MEDIA_ALPHA_BACKDROP_CLASSES,
              isInvalid && MEDIA_INVALID_CLASSES,
              isDragging && MEDIA_DROPZONE_ACTIVE_CLASSES,
            )}
            onClick={openFilePicker}
            onDragOver={(event) => {
              // Without this the browser navigates to the dropped file and takes
              // whatever the operator had typed on the page with it.
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              void upload(event.dataTransfer.files);
            }}
          >
            {previewContent}
          </button>
        ) : (
          <div
            ref={previewRef}
            className={cn(
              MEDIA_PREVIEW_CLASSES,
              state === 'ready' && MEDIA_ALPHA_BACKDROP_CLASSES,
              isInvalid && MEDIA_INVALID_CLASSES,
            )}
          >
            {previewContent}
          </div>
        )}

        {asset === null || size === null ? null : (
          <p className={MEDIA_FACTS_CLASSES}>
            {asset.width === null || asset.height === null ? null : (
              // `dir="ltr"`, and it is load-bearing: the STRING is
              // "1,200 × 300", but under an RTL paragraph the bidi algorithm
              // reorders the two numbers around the neutral `×` and an Arabic
              // operator reads "300 × 1,200" — the height first. A 4:1 banner
              // reads as a portrait. The stills caught it; no measurement could,
              // because the text content is correct either way. Same fix as
              // P10.2b's `valueDir: 'ltr'` for URLs.
              <span dir="ltr" className={MEDIA_FACT_CLASSES}>
                {t('media.dimensions', {
                  width: formatNumber(asset.width, locale),
                  height: formatNumber(asset.height, locale),
                })}
              </span>
            )}
            <span className={MEDIA_FACT_CLASSES}>
              {t('media.size', {
                value: formatNumber(size.value, locale),
                unit: t(`media.unit.${size.unit}`),
              })}
            </span>
            <span className={MEDIA_FACT_CLASSES}>{asset.mime}</span>
          </p>
        )}
      </MediaShell>

      <MediaLibraryDialog
        isOpen={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        kind={kind}
        value={value}
        source={source}
        onUploadRequested={() => {
          setIsLibraryOpen(false);
          openFilePicker();
        }}
        onChoose={(mediaId) => {
          choose(mediaId);
          setIsLibraryOpen(false);
        }}
      />
    </>
  );
});
